import {
    Component,
    OnDestroy,
    OnInit,
    Signal,
    ChangeDetectionStrategy,
} from '@angular/core';
import { StepNavigationService } from '../../../../modules/application/shared/services/step-navigation/step-navigation.service';
import { StepLink } from '../../../../modules/application/shared/models/step-link';
import { CommonModule } from '@angular/common';
import { Subject, filter, takeUntil } from 'rxjs';
import { EllipsisPipe } from '../../../pipes/ellipsis.pipe';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { RouteConstants } from '../../../../core/constants/routes';
import { ApplicationStateService } from '../../../../modules/application/shared/services/application-state/application-state.service';

@Component({
    selector: 'app-navigation-steps',
    templateUrl: './application-navigation-steps.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, EllipsisPipe, RouterModule],
})
export class ApplicationNavigationStepsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    canProceedDirectlyToStep: boolean = false;

    steps: StepLink[] = [];
    currentStep: StepLink | undefined;

    isApplicationTemplate: Signal<boolean> =
        this.applicationStateService.IsApplicationTemplate;

    constructor(
        private readonly applicationStateService: ApplicationStateService,
        private readonly stepService: StepNavigationService,
        private readonly router: Router,
    ) {}

    ngOnInit(): void {
        this.stepService.currentStep$
            .pipe(takeUntil(this.destroy$))
            .subscribe((currentStep) => {
                this.currentStep = currentStep;
            });

        this.stepService.steps$
            .pipe(takeUntil(this.destroy$))
            .subscribe((steps) => {
                this.steps = steps;
            });

        this.intializeAndCheckCanProceedDirectlyToStep();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    proceedToStep(step: StepLink): void {
        this.stepService.triggerProceedToStep(step);
    }

    /**
     * Sets the initial component load to check if a new application page is loaded. In addition,
     * we continue to check all new page navigations to verify whether the current page is still part
     * of the new application flow.
     */
    private intializeAndCheckCanProceedDirectlyToStep() {
        this.canProceedDirectlyToStep = this.getCanProceedDirectlyToStep();

        this.router.events
            .pipe(
                takeUntil(this.destroy$),
                filter((event) => event instanceof NavigationEnd),
            )
            .subscribe(() => {
                this.canProceedDirectlyToStep =
                    this.getCanProceedDirectlyToStep();
            });
    }

    private getCanProceedDirectlyToStep(): boolean {
        const url = this.router.url;
        const isApplicationStepsBlocked =
            RouteConstants.Application.isApplicationRouteWhereStepsCannotBeDirectlyNavigatedTo(
                url,
            );

        const isFrnStepsBlocked =
            RouteConstants.FrnFiling.isRouteWhereStepsCannotBeDirectlyNavigatedTo(
                url,
            );

        return !(isApplicationStepsBlocked || isFrnStepsBlocked);
    }
}
